import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FREE_TRIAL_TOTAL, getFreeQuestionsUsed } from '../components/FreeTrialBanner.jsx';
import { CLINICALS_PLANS_MONTHLY } from '../data/clinicalPlans.js';
import PromoBanner from '../components/PromoBanner.jsx';
import { usePricingSectionView } from '../hooks/usePricingSectionView.js';

// ── Audience tabs ────────────────────────────────────────────────────────────

const AUDIENCES = [
    {
        id: 'ms1_ms2',
        label: 'MS1 / MS2',
        badge: 'Class Prep',
        badgeColor: 'fuchsia',
        headline: 'Medical School Block Exams',
        desc: 'Generate questions directly from your professor\'s lecture notes — styled for block exams in each preclinical subject. No board prep fluff, just what\'s on your test next week.',
        exams: [
            'Anatomy', 'Biochemistry', 'Physiology', 'Pharmacology',
            'Microbiology', 'Pathology', 'Immunology', 'Neuroscience',
        ],
        questionStyle: 'Mechanism & structural knowledge questions',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
        ),
    },
    {
        id: 'ms3_ms4',
        label: 'MS3 / MS4',
        badge: 'Shelf & Step',
        badgeColor: 'violet',
        headline: 'Shelf Exams & National Boards',
        desc: 'Clinical vignette-style questions from your rotation notes and lecture slides. Build toward shelf exams and Step 1/2 simultaneously — every question is tied to your current clerkship.',
        exams: [
            'Shelf Exams', 'USMLE Step 1', 'USMLE Step 2 CK',
            'COMLEX Level 1', 'COMLEX Level 2',
        ],
        questionStyle: 'Clinical vignette with diagnosis & management focus',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
        ),
    },
    {
        id: 'nursing',
        label: 'Nursing Student',
        badge: 'NCLEX Prep',
        badgeColor: 'pink',
        headline: 'Nursing Block Exams & NCLEX',
        desc: 'Questions written in NCLEX style — priority nursing action, delegation, therapeutic communication, and pathophysiology applied to nursing care. Study from your own lecture slides and syllabi.',
        exams: [
            'NCLEX-RN', 'NCLEX-PN', 'Fundamentals', 'Medical-Surgical', 'Maternal-Newborn',
            'Pediatric Nursing', 'Psychiatric-Mental Health',
            'Community & Public Health', 'Leadership & Delegation',
            'Pharmacology for Nursing',
        ],
        questionStyle: 'Priority action, delegation, ADPIE nursing process',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
        ),
    },
    {
        id: 'np_pa_aa',
        label: 'NP / PA / AA Student',
        badge: 'Class Prep',
        badgeColor: 'fuchsia',
        headline: 'Allied Health Didactic Phase',
        desc: 'Same powerful class prep engine used by medical students — questions generated from your course materials for each preclinical block subject in your NP, PA, or AA program.',
        exams: [
            'Anatomy', 'Physiology', 'Pharmacology', 'Pathology',
            'Microbiology', 'Biochemistry', 'Immunology', 'Neuroscience',
        ],
        questionStyle: 'Mechanism & clinical foundation questions',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
        ),
    },
    {
        id: 'resident',
        label: 'Resident / Fellow',
        badge: 'Board Review',
        badgeColor: 'violet',
        headline: 'Licensure & Specialty Boards',
        desc: 'High-yield vignettes targeting Step 3, COMLEX 3, and specialty board content — management sequencing, guideline thresholds, contraindications, and the exceptions that only trainees miss.',
        exams: [
            'USMLE Step 3', 'COMLEX Level 3',
            'Internal Medicine (ABIM)', 'General Surgery (ABS)',
            'Emergency Medicine (ABEM)', 'OB/GYN (ABOG)',
            'Psychiatry (ABPN)', 'Family Medicine (ABFM)',
            'Pediatrics (ABP)',
        ],
        questionStyle: 'Three-step reasoning, guideline anchored',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
        ),
    },
    {
        id: 'np_pa_cert',
        label: 'NP / PA / CRNA / CAA',
        badge: 'Certification',
        badgeColor: 'violet',
        headline: 'Advanced Practice Licensure',
        desc: 'Questions calibrated to your specific certification exam — FNP, ACNP, PANCE, CRNA, or CAA — covering your content blueprint with the clinical management depth your boards require.',
        exams: [
            'FNP (AANP / ANCC)',
            'Acute Care NP (ACNP)',
            'PA-C (PANCE / PANRE)',
            'CRNA (NBCRNA NCE)',
            'CAA (NCCAA)',
        ],
        questionStyle: 'Diagnosis, management & scope-of-practice focus',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
        ),
    },
    {
        id: 'attending',
        label: 'Attending / MOC',
        badge: 'Specialty Boards',
        badgeColor: 'teal',
        headline: 'Initial Certification & MOC',
        desc: 'Specialty board-level questions that assume you already know first-line management. Every question tests the exception, the contraindication, the named second-line agent, and the guideline threshold — the things that separate specialists.',
        exams: [
            'Internal Medicine (ABIM)',
            'Family Medicine (ABFM)',
            'General Surgery (ABS)',
            'Emergency Medicine (ABEM)',
            'OB/GYN (ABOG)',
            'Pediatrics (ABP)',
            'Psychiatry (ABPN)',
            'Dermatology (ABD)',
            'Ophthalmology (ABO)',
            'Otolaryngology (ABOto)',
        ],
        questionStyle: 'Guideline-anchored, specialist-level exceptions',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
        ),
    },
];

const BADGE_COLORS = {
    fuchsia: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/25',
    violet:  'bg-violet-500/15  text-violet-300  border-violet-400/25',
    pink:    'bg-pink-500/15    text-pink-300    border-pink-400/25',
    teal:    'bg-teal-500/15    text-teal-300    border-teal-400/25',
};

const EXAM_TAG_COLORS = {
    fuchsia: 'bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-400/20',
    violet:  'bg-violet-500/10  text-violet-300  border-violet-400/20',
    pink:    'bg-pink-500/10    text-pink-300    border-pink-400/20',
    teal:    'bg-teal-500/10    text-teal-300    border-teal-400/20',
};

// ── How it works steps ───────────────────────────────────────────────────────

const STEPS = [
    {
        n: '1',
        title: 'Paste your notes',
        desc: 'Drop in lecture slides, class notes, a photo of the whiteboard, or record live audio. Any format works.',
    },
    {
        n: '2',
        title: 'Questions generated for your exam',
        desc: 'MCQs styled exactly for your program — block exam, shelf, Step, NCLEX, boards. Not generic question banks.',
    },
    {
        n: '3',
        title: 'Build your personal Q-bank',
        desc: 'Every question you generate is saved. Review by subject, track your accuracy, study the ones you miss.',
    },
];

// ── Component ────────────────────────────────────────────────────────────────

export default function FlashClinicalLandingPage({ entitlements, onOpenAccount, onOpenPlanChooser }) {
    const pricingSectionRef = usePricingSectionView('/clinicals');
    const navigate     = useNavigate();
    const [activeTab, setActiveTab] = useState('ms1_ms2');

    const questionsUsed  = getFreeQuestionsUsed();
    const freeRemaining  = Math.max(0, FREE_TRIAL_TOTAL - questionsUsed);
    const hasFreeLeft    = freeRemaining > 0;
    const isSubscribed   = entitlements?.tier === 'pro' || entitlements?.tier === 'clinicals';
    const canAccess      = isSubscribed || hasFreeLeft;
    const isAnonymous    = !entitlements?.uid || entitlements?.uid === 'anonymous';

    const handleLaunch = () => {
        if (!canAccess) {
            if (isAnonymous) { onOpenAccount?.(); } else { onOpenPlanChooser?.(); }
            return;
        }
        navigate('/app/home?clinical=1');
    };

    const activeAudience = AUDIENCES.find(a => a.id === activeTab) ?? AUDIENCES[0];
    const color          = activeAudience.badgeColor;

    return (
        <div className="min-h-screen text-slate-200" style={{ backgroundColor: '#080d18', fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif' }}>

            {/* Background glows */}
            <div aria-hidden="true" className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-96 w-[52rem] rounded-full bg-teal-500/8 blur-3xl" />
                <div className="absolute top-1/3 -right-24 h-80 w-80 rounded-full bg-emerald-500/8 blur-3xl" />
                <div className="absolute bottom-0 -left-16 h-80 w-80 rounded-full bg-teal-600/6 blur-3xl" />
            </div>

            {/* ── Nav ───────────────────────────────────────────────── */}
            <nav className="relative z-10 border-b border-white/5 bg-black/20 backdrop-blur-sm">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 flex items-center justify-between h-14">
                    <div className="flex items-center gap-3">
                        <a href="/" className="text-slate-400 hover:text-white text-sm transition flex items-center gap-1.5">
                            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5M12 19l-7-7 7-7" />
                            </svg>
                            FlashFonic
                        </a>
                        <span className="h-4 w-px bg-white/10" />
                        <span className="text-sm font-semibold text-white flex items-center gap-2">
                            FlashFonic
                            <span className="rounded-full bg-teal-500/20 border border-teal-400/25 px-2 py-0.5 text-[10px] font-semibold text-teal-300 uppercase tracking-wide">Beta</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onOpenAccount}
                            className="text-sm text-slate-400 hover:text-white transition"
                        >
                            {isAnonymous ? 'Sign in' : 'Account'}
                        </button>
                        {canAccess && (
                            <button
                                onClick={handleLaunch}
                                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:opacity-90 px-4 py-1.5 text-sm font-medium text-white transition shadow-lg shadow-teal-500/20"
                            >
                                Open Workspace
                            </button>
                        )}
                    </div>
                </div>
            </nav>

            {/* ── Hero ──────────────────────────────────────────────── */}
            <section className="relative z-10 pt-20 pb-14 px-4 sm:px-6 text-center">
                <div className="mx-auto max-w-3xl">
                    <div className="inline-flex items-center gap-2 rounded-full border border-teal-400/25 bg-teal-500/10 px-4 py-1.5 text-xs font-medium text-teal-300 mb-7">
                        <span className="h-1.5 w-1.5 rounded-full bg-teal-400 animate-pulse" />
                        Built for every stage of healthcare training
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
                        Your notes.<br />
                        <span className="bg-gradient-to-r from-teal-400 via-emerald-400 to-cyan-300 bg-clip-text text-transparent">
                            Your exam. Your questions.
                        </span>
                    </h1>
                    <p className="mt-6 text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
                        Paste your lecture notes and get boards-ready MCQs styled precisely for your program — from MS1 block exams and NCLEX to USMLE Step 3 and specialty board certification.
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        {canAccess ? (
                            <button
                                onClick={handleLaunch}
                                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 px-8 py-4 text-base font-semibold text-white shadow-xl shadow-teal-500/25 transition"
                            >
                                {isSubscribed ? 'Open Workspace' : `Start free — ${freeRemaining} question${freeRemaining !== 1 ? 's' : ''} included`}
                                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </button>
                        ) : (
                            <button
                                onClick={() => { if (isAnonymous) onOpenAccount?.(); else onOpenPlanChooser?.(); }}
                                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 px-8 py-4 text-base font-semibold text-white shadow-xl shadow-teal-500/25 transition"
                            >
                                {isAnonymous ? 'Sign up free' : 'Unlock full access'}
                                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </button>
                        )}
                        <p className="text-sm text-slate-500">
                            {isSubscribed
                                ? 'Full access active'
                                : hasFreeLeft
                                    ? `${freeRemaining} of ${FREE_TRIAL_TOTAL} free questions remaining — no credit card`
                                    : 'Free trial complete — subscribe to continue'}
                        </p>
                    </div>

                    {!isSubscribed && (
                        <div className="mt-5 inline-flex flex-col items-center gap-1.5">
                            <div className="w-48 h-1.5 rounded-full bg-white/8 overflow-hidden">
                                <div
                                    className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-400 transition-all"
                                    style={{ width: `${Math.min(100, (questionsUsed / FREE_TRIAL_TOTAL) * 100)}%` }}
                                />
                            </div>
                            <span className="text-xs text-slate-600">{questionsUsed} / {FREE_TRIAL_TOTAL} free questions used</span>
                        </div>
                    )}
                </div>
            </section>

            {/* ── Who it's for ──────────────────────────────────────── */}
            <section className="relative z-10 py-16 px-4 sm:px-6">
                <div className="mx-auto max-w-5xl">
                    <div className="text-center mb-10">
                        <h2 className="text-2xl sm:text-3xl font-bold text-white">Who it's for</h2>
                        <p className="mt-2 text-slate-400 text-sm">Every role. Every exam. Questions styled for exactly where you are in training.</p>
                    </div>

                    {/* Tab bar */}
                    <div className="flex flex-wrap justify-center gap-2 mb-8">
                        {AUDIENCES.map(a => (
                            <button
                                key={a.id}
                                onClick={() => setActiveTab(a.id)}
                                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium border transition ${
                                    activeTab === a.id
                                        ? BADGE_COLORS[a.badgeColor] + ' shadow-sm'
                                        : 'border-white/10 text-slate-400 hover:text-slate-200 hover:border-white/20'
                                }`}
                            >
                                <span className={`shrink-0 ${activeTab === a.id ? '' : 'text-slate-500'}`}>{a.icon}</span>
                                {a.label}
                            </button>
                        ))}
                    </div>

                    {/* Tab content */}
                    <div className={`rounded-2xl border p-6 sm:p-8 transition-all ${
                        color === 'teal'    ? 'border-teal-400/20 bg-teal-500/5' :
                        color === 'pink'    ? 'border-pink-400/20 bg-pink-500/5' :
                        color === 'violet'  ? 'border-violet-400/20 bg-violet-500/5' :
                                             'border-fuchsia-400/20 bg-fuchsia-500/5'
                    }`}>
                        <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                            {/* Left: description */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2.5 mb-3">
                                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${BADGE_COLORS[color]}`}>
                                        {activeAudience.badge}
                                    </span>
                                </div>
                                <h3 className="text-xl font-semibold text-white mb-2">{activeAudience.headline}</h3>
                                <p className="text-sm text-slate-400 leading-relaxed mb-5">{activeAudience.desc}</p>

                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                    <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                                    Question style: <span className="text-slate-300">{activeAudience.questionStyle}</span>
                                </div>

                                <button
                                    onClick={handleLaunch}
                                    className={`mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium text-white transition ${
                                        color === 'teal'   ? 'bg-teal-500 hover:bg-teal-400 shadow-lg shadow-teal-500/20' :
                                        color === 'pink'   ? 'bg-pink-500 hover:bg-pink-400 shadow-lg shadow-pink-500/20' :
                                        color === 'violet' ? 'bg-violet-500 hover:bg-violet-400 shadow-lg shadow-violet-500/20' :
                                                             'bg-fuchsia-500 hover:bg-fuchsia-400 shadow-lg shadow-fuchsia-500/20'
                                    }`}
                                >
                                    {!hasFreeLeft ? 'Upgrade to continue' : `Try free — ${freeRemaining} question${freeRemaining !== 1 ? 's' : ''} left`}
                                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 12h14M12 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>

                            {/* Right: exam tags */}
                            <div className="sm:w-60 shrink-0">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-3">Supported exams</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {activeAudience.exams.map(exam => (
                                        <span
                                            key={exam}
                                            className={`inline-block rounded-full border px-2.5 py-1 text-[11px] font-medium ${EXAM_TAG_COLORS[color]}`}
                                        >
                                            {exam}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── How it works ──────────────────────────────────────── */}
            <section className="relative z-10 py-16 px-4 sm:px-6 border-t border-white/5">
                <div className="mx-auto max-w-4xl">
                    <h2 className="text-center text-2xl font-bold text-white mb-12">How it works</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {STEPS.map((s, i) => (
                            <div key={i} className="relative">
                                {i < STEPS.length - 1 && (
                                    <div className="hidden sm:block absolute top-5 left-full w-full h-px bg-gradient-to-r from-white/10 to-transparent -translate-x-4 z-0" />
                                )}
                                <div className="relative z-10 flex flex-col items-start gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500/20 to-emerald-500/20 border border-teal-400/20 text-sm font-bold text-teal-300">
                                        {s.n}
                                    </div>
                                    <h3 className="text-sm font-semibold text-white">{s.title}</h3>
                                    <p className="text-xs text-slate-400 leading-relaxed">{s.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Features compact grid ─────────────────────────────── */}
            <section className="relative z-10 py-16 px-4 sm:px-6 border-t border-white/5">
                <div className="mx-auto max-w-5xl">
                    <h2 className="text-center text-2xl font-bold text-white mb-10">Everything built in</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[
                            {
                                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />,
                                title: 'Exam-tuned question format',
                                desc: 'MS1 block exams get direct mechanism questions. Step 2 and NCLEX get vignettes. Boards get 400-word cases. Format matches your actual exam.',
                            },
                            {
                                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />,
                                title: 'From your own source material',
                                desc: 'Paste professor notes, upload a photo of the whiteboard, or record live audio. The engine extracts the concept and builds the question.',
                            },
                            {
                                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />,
                                title: 'Audio & image capture',
                                desc: 'Record live lecture audio or snap your slides. Transcribed and turned into questions automatically.',
                            },
                            {
                                icon: <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></>,
                                title: 'Accuracy checking built in',
                                desc: 'Every question passes a factual accuracy check before you see it. Incorrect questions are rejected and regenerated automatically.',
                            },
                            {
                                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
                                title: 'Personal Q-bank that grows',
                                desc: 'Every question you generate is banked by subject. Review performance by topic, retry wrong answers, and watch your accuracy improve.',
                            },
                            {
                                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />,
                                title: 'Detailed rationales & teaching points',
                                desc: 'Every question includes a full explanation of why each answer is right or wrong — plus the teaching point to carry into the exam.',
                            },
                        ].map((f, i) => (
                            <div key={i} className="rounded-2xl border border-white/6 bg-white/2 p-5 hover:border-teal-500/20 hover:bg-teal-500/3 transition group">
                                <div className="h-9 w-9 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center mb-4">
                                    <svg className="h-5 w-5 text-teal-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        {f.icon}
                                    </svg>
                                </div>
                                <h3 className="text-sm font-semibold text-white mb-1.5">{f.title}</h3>
                                <p className="text-xs text-slate-400 leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Pricing ───────────────────────────────────────────── */}
            <section ref={pricingSectionRef} className="relative z-10 py-16 px-4 sm:px-6 border-t border-white/5">
                <div className="mx-auto max-w-xl">
                    <div className="text-center mb-10">
                        <h2 className="text-2xl font-bold text-white">Simple pricing</h2>
                        <p className="text-slate-400 text-sm mt-2">Try free · 1 credit = 1 question or flashcard generated · Credits refresh monthly</p>
                    </div>

                    <PromoBanner className="mb-8" />

                    {/* Free trial card */}
                    <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-6 mb-4">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h3 className="text-base font-semibold text-white">Free Trial</h3>
                                <div className="mt-1 flex items-baseline gap-1">
                                    <span className="text-2xl font-bold text-white">$0</span>
                                    <span className="text-slate-400 text-sm">to start</span>
                                </div>
                            </div>
                            <button
                                onClick={handleLaunch}
                                disabled={!hasFreeLeft}
                                className="shrink-0 rounded-xl px-4 py-2 text-sm font-medium border border-white/10 text-slate-300 hover:text-white hover:border-white/20 transition disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {hasFreeLeft ? `Start free (${freeRemaining} left)` : 'Trial complete'}
                            </button>
                        </div>
                        <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-slate-400">
                            <li className="flex items-start gap-1.5"><span className="text-teal-400">✓</span> {FREE_TRIAL_TOTAL} free question generations</li>
                            <li className="flex items-start gap-1.5"><span className="text-teal-400">✓</span> Step 1, Step 2, Shelf & more</li>
                            <li className="flex items-start gap-1.5 text-slate-600"><span>✗</span> Monthly credit allowance</li>
                            <li className="flex items-start gap-1.5 text-slate-600"><span>✗</span> Batch up to 15 cards</li>
                        </ul>
                    </div>

                    {/* 3-tier paid plans — rendered from the same CLINICALS_PLANS_MONTHLY data as the
                        checkout modal (PlanChooserModal.jsx), so price/credits/features can't drift
                        between the marketing page and the actual purchase flow. */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {CLINICALS_PLANS_MONTHLY.map((plan) => {
                            const isBoardReview = plan.id === 'board_review_monthly';
                            return (
                                <div
                                    key={plan.id}
                                    className={`rounded-2xl border p-5 relative overflow-hidden ${plan.popular ? '' : 'border-white/10 bg-slate-900/40'}`}
                                    style={plan.popular ? { borderColor: `${plan.color}59`, background: `${plan.color}0f` } : undefined}
                                >
                                    {plan.popular && (
                                        <div
                                            className="absolute top-2.5 right-2.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                                            style={{ background: `${plan.color}33`, borderColor: `${plan.color}26`, color: plan.color }}
                                        >
                                            Popular
                                        </div>
                                    )}
                                    <h3 className="text-sm font-semibold" style={{ color: plan.color }}>{plan.name}</h3>
                                    <div className="mt-1 mb-4">
                                        <span className="text-2xl font-bold text-white">{plan.price}</span>
                                        <span className="text-slate-500 text-xs ml-1">{plan.period}</span>
                                    </div>
                                    <ul className="space-y-1.5 text-xs text-slate-300 mb-5">
                                        {plan.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-start gap-1.5">
                                                <span style={{ color: plan.color }}>✓</span> {feature}
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => {
                                            if (isBoardReview && isSubscribed) { handleLaunch(); return; }
                                            if (isAnonymous) { onOpenAccount?.(); } else { onOpenPlanChooser?.({ isClinicals: true }); }
                                        }}
                                        className={
                                            plan.popular
                                                ? 'w-full rounded-xl py-2 text-xs font-semibold bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white transition shadow-lg shadow-teal-500/20'
                                                : 'w-full rounded-xl py-2 text-xs font-semibold border transition hover:bg-white/5'
                                        }
                                        style={!plan.popular ? { borderColor: `${plan.color}4d`, color: plan.color } : undefined}
                                    >
                                        {isSubscribed ? (isBoardReview ? 'Open Workspace' : 'Manage plan') : `Get ${plan.name}`}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-center text-xs text-slate-600 mt-4">Cancel anytime. No contracts. Extra credit packs available for subscribers.</p>
                </div>
            </section>

            {/* ── Footer ────────────────────────────────────────────── */}
            <footer className="relative z-10 border-t border-white/5 py-8 px-4 text-center text-xs text-slate-600">
                <p>
                    FlashFonic Clinicals is part of{' '}
                    <a href="/" className="text-slate-500 hover:text-white transition">FlashFonic</a>
                    {' '}— Exam prep built for clinicians, by clinicians.
                </p>
                <p className="mt-1">Generated questions. Not a substitute for clinical judgment or official study resources.</p>
            </footer>
        </div>
    );
}
